import React from "react";
import { Trash2 } from "lucide-react";

// Cross-browser confirmation modal for any high-stakes action.
// `window.confirm()` is unreliable in preview iframes and breaks the
// flow there, so every destructive or financial action must route
// through this.
//
// Tone presets:
//   - "destructive" (default) : red — for Delete / Reject
//   - "primary"               : blue — for Approve / Confirm payment
export default function ConfirmDialog({
  open,
  title = "Delete this item?",
  description = "This cannot be undone.",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  busy = false,
  busyLabel,
  tone = "destructive",
  icon: Icon,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;
  const isPrimary = tone === "primary";
  const IconCmp = Icon || Trash2;
  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={() => !busy && onCancel?.()}
        data-testid="confirm-dialog-overlay"
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="confirm-dialog"
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        onClick={() => !busy && onCancel?.()}
      >
        <div
          className="w-full max-w-md rounded-xl bg-white border border-[#E5E7EB] shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{ animation: "fadeUp 0.18s ease-out" }}
        >
          <div className="p-6">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-4 ${
              isPrimary ? "bg-[#EFF6FF] text-[#002FA7]" : "bg-[#FEF2F2] text-[#EF4444]"
            }`}>
              <IconCmp size={22} />
            </div>
            <div className="font-display font-black text-xl text-[#111827] mb-2">{title}</div>
            <p className="text-sm text-[#4B5563] leading-relaxed">{description}</p>
          </div>
          <div className="flex items-center justify-end gap-2 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
            <button
              onClick={onCancel}
              disabled={busy}
              data-testid="confirm-dialog-cancel"
              className="inline-flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-bold text-[#111827] hover:border-[#111827] disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              data-testid="confirm-dialog-confirm"
              className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${
                isPrimary ? "bg-[#002FA7] hover:bg-[#001f70]" : "bg-[#EF4444] hover:bg-[#DC2626]"
              }`}
            >
              {busy ? (busyLabel || (isPrimary ? "Working…" : "Deleting…")) : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
